import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { toast } from 'sonner';

// Shadcn UI Components
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from './components/ui/avatar';

// Icons
import { 
  DollarSign, 
  Heart, 
  Send, 
  Loader2, 
  Gift,
  CheckCircle,
  XCircle,
  MessageSquare,
  TrendingUp,
  Users,
  Sparkles,
  Settings,
  ArrowLeft
} from 'lucide-react';

import { BACKEND_URL, API } from './config';

// Tip Button Component - Main tipping interface for posts
export const TipButton = ({ 
  post, 
  creatorName, 
  className = "",
  variant = "default" // "default", "compact", "icon-only"
}) => {
  const [showTipModal, setShowTipModal] = useState(false);
  const [tipAmount, setTipAmount] = useState(5);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [customAmount, setCustomAmount] = useState(false);

  const minTip = post?.min_tip_amount || 5;
  const tipEnabled = post?.tip_enabled !== false;

  const presetAmounts = [5, 10, 20, 50];

  const handleTip = async () => {
    if (tipAmount < minTip) {
      toast.error(`Minimum tip is $${minTip}`);
      return;
    }

    setLoading(true);
    try {
      const sessionToken = Cookies.get('session_token');
      if (!sessionToken) {
        toast.error('Please log in to send a tip');
        setLoading(false);
        return;
      }

      const response = await axios.post(`${API}/tips/checkout`, {
        post_id: post.id,
        amount: tipAmount,
        message: message,
        origin_url: window.location.origin
      }, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });

      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
      
    } catch (error) {
      console.error('Tip error:', error);
      // Handle authentication errors specifically
      if (error.response?.status === 401) {
        toast.error('Your session has expired. Please log in again.');
        // Clear stale cookies with proper path
        const cookieOptions = { path: '/' };
        Cookies.remove('session_token', cookieOptions);
        Cookies.remove('user_data', cookieOptions);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to process tip');
      }
      setLoading(false);
    }
  };

  if (!tipEnabled) {
    return null;
  }

  // Icon-only variant for compact displays
  if (variant === "icon-only") {
    return (
      <>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowTipModal(true)}
          className={`text-pink-500 hover:text-pink-600 hover:bg-pink-50 ${className}`}
          data-testid="tip-button-icon"
        >
          <Gift className="h-4 w-4" />
        </Button>
        <TipModal
          open={showTipModal}
          onClose={() => setShowTipModal(false)}
          post={post}
          creatorName={creatorName}
          tipAmount={tipAmount}
          setTipAmount={setTipAmount}
          message={message}
          setMessage={setMessage}
          customAmount={customAmount}
          setCustomAmount={setCustomAmount}
          presetAmounts={presetAmounts}
          minTip={minTip}
          loading={loading}
          onTip={handleTip}
        />
      </>
    );
  }

  // Compact variant
  if (variant === "compact") {
    return (
      <>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowTipModal(true)}
          className={`border-pink-200 text-pink-600 hover:bg-pink-50 ${className}`}
          data-testid="tip-button-compact"
        >
          <Gift className="h-4 w-4 mr-1" />
          Tip
        </Button>
        <TipModal
          open={showTipModal}
          onClose={() => setShowTipModal(false)}
          post={post}
          creatorName={creatorName}
          tipAmount={tipAmount}
          setTipAmount={setTipAmount}
          message={message}
          setMessage={setMessage}
          customAmount={customAmount}
          setCustomAmount={setCustomAmount}
          presetAmounts={presetAmounts}
          minTip={minTip}
          loading={loading}
          onTip={handleTip}
        />
      </>
    );
  }

  // Default variant
  return (
    <>
      <Button
        onClick={() => setShowTipModal(true)}
        className={`bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white ${className}`}
        data-testid="tip-button"
      >
        <Gift className="h-4 w-4 mr-2" />
        Send Tip
      </Button>
      <TipModal
        open={showTipModal}
        onClose={() => setShowTipModal(false)}
        post={post}
        creatorName={creatorName}
        tipAmount={tipAmount}
        setTipAmount={setTipAmount}
        message={message}
        setMessage={setMessage}
        customAmount={customAmount}
        setCustomAmount={setCustomAmount}
        presetAmounts={presetAmounts}
        minTip={minTip}
        loading={loading}
        onTip={handleTip}
      />
    </>
  );
};

// Tip Modal Component
const TipModal = ({ 
  open, 
  onClose, 
  post, 
  creatorName,
  tipAmount,
  setTipAmount,
  message,
  setMessage,
  customAmount,
  setCustomAmount,
  presetAmounts,
  minTip,
  loading,
  onTip
}) => {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Gift className="h-5 w-5 text-pink-500" />
            <span>Send a Tip</span>
          </DialogTitle>
          <DialogDescription>
            Show appreciation to {creatorName} for this post
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Post Preview */}
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-800 line-clamp-2">
              {post?.title || 'Untitled Post'}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              by {creatorName}
            </p>
          </div>

          {/* Preset Amounts */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-700">Select Amount ($)</label>
            <div className="grid grid-cols-4 gap-2">
              {presetAmounts.map((amount) => (
                <button
                  key={amount}
                  onClick={() => {
                    setTipAmount(amount);
                    setCustomAmount(false);
                  }}
                  className={`py-2 px-3 rounded-lg border-2 font-semibold text-sm transition-all ${
                    tipAmount === amount && !customAmount
                      ? 'border-pink-500 bg-pink-50 text-pink-600'
                      : 'border-gray-200 hover:border-pink-300 text-gray-700'
                  }`}
                  data-testid={`tip-preset-${amount}`}
                >
                  ${amount}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Amount */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Or enter custom amount (min ${minTip})
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">$</span>
              <Input
                type="number"
                min={minTip}
                step="0.01"
                value={customAmount ? tipAmount : ''}
                onChange={(e) => {
                  setCustomAmount(true);
                  setTipAmount(parseFloat(e.target.value) || minTip);
                }}
                onFocus={() => setCustomAmount(true)}
                placeholder={`${minTip}.00`}
                className="pl-8"
                data-testid="tip-custom-amount"
              />
            </div>
          </div>

          {/* Message */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Add a message (optional)
            </label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Great content! Keep it up!"
              maxLength={200}
              rows={3}
              data-testid="tip-message"
            />
            <p className="text-xs text-gray-400 text-right">{message.length}/200</p>
          </div>

          {/* Summary */}
          <div className="p-4 bg-gradient-to-r from-pink-50 to-rose-50 rounded-lg border border-pink-100">
            <div className="flex items-center justify-between">
              <span className="text-gray-700 font-medium">Total Tip Amount</span>
              <span className="text-2xl font-bold text-pink-600">
                ${tipAmount.toFixed(2)}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <Button 
              variant="outline" 
              onClick={onClose} 
              className="flex-1"
              data-testid="tip-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={onTip}
              disabled={loading || tipAmount < minTip}
              className="flex-1 bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white"
              data-testid="tip-confirm"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Processing...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send ${tipAmount.toFixed(2)}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Tip Success Page Component
export const TipSuccessPage = () => {
  const [status, setStatus] = useState('checking');
  const [tipDetails, setTipDetails] = useState(null);

  useEffect(() => {
    const checkTipStatus = async (sessionId) => {
      try {
        const sessionToken = Cookies.get('session_token');
        const response = await axios.get(`${API}/payments/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        });
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          setTipDetails(response.data);
        } else {
          setStatus('pending');
        }
      } catch (err) {
        console.error('Error checking tip status:', err);
        setStatus('error');
      }
    };
    
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    
    if (sessionId) {
      checkTipStatus(sessionId);
    } else {
      Promise.resolve().then(() => setStatus('error'));
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-rose-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-6">
          {status === 'checking' && (
            <div className="text-center py-8">
              <Loader2 className="h-12 w-12 animate-spin text-pink-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold">Verifying your tip...</h2>
              <p className="text-gray-500 mt-2">Please wait a moment</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Tip Sent!</h2>
              <p className="text-gray-500 mb-4">
                Your tip of ${tipDetails?.amount?.toFixed(2) || '0.00'} was sent successfully
              </p>
              <div className="p-4 bg-pink-50 rounded-lg mb-6">
                <Heart className="h-6 w-6 text-pink-500 mx-auto mb-2" />
                <p className="text-sm text-gray-600">
                  Thank you for supporting the creator!
                </p>
              </div>
              <Button
                onClick={() => window.location.href = '/dashboard'}
                className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white"
                data-testid="tip-success-home"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Feed
              </Button>
            </div>
          )}

          {status === 'pending' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Loader2 className="h-8 w-8 text-yellow-500 animate-spin" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Processing...</h2>
              <p className="text-gray-500">
                Your tip is being processed. This may take a moment.
              </p>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="h-8 w-8 text-red-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h2>
              <p className="text-gray-500 mb-4">
                We could not verify your tip. Please contact support if you were charged.
              </p>
              <Button
                variant="outline"
                onClick={() => window.location.href = '/dashboard'}
                data-testid="tip-error-home"
              >
                Back to Feed
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Tip Cancelled Page Component
export const TipCancelledPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="h-8 w-8 text-gray-400" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Tip Cancelled</h2>
            <p className="text-gray-500 mb-6">
              No worries! You can tip the creator anytime.
            </p>
            <Button
              onClick={() => window.location.href = '/dashboard'}
              className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white"
              data-testid="tip-cancelled-home"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Feed
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Post Tips Display Component - Shows tip stats on a post
export const PostTipStats = ({ post, showDetails = false }) => {
  const totalTips = post?.total_tips_received || 0;
  const tipCount = post?.tip_count || 0;

  if (tipCount === 0) return null;

  return (
    <div className="flex items-center space-x-2 text-sm text-gray-500">
      <Gift className="h-4 w-4 text-pink-500" />
      <span className="font-medium text-pink-600">${totalTips.toFixed(2)}</span>
      {showDetails && (
        <span>from {tipCount} {tipCount === 1 ? 'tip' : 'tips'}</span>
      )}
    </div>
  );
};

// Tip Settings Form Component for Post Creation/Editing
export const TipSettingsForm = ({ 
  tipEnabled, 
  setTipEnabled, 
  minTipAmount, 
  setMinTipAmount 
}) => {
  return (
    <div className="space-y-4 p-4 border border-pink-200 rounded-lg bg-pink-50">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Gift className="h-5 w-5 text-pink-500" />
          <span className="font-medium">Tipping</span>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={tipEnabled}
            onChange={(e) => setTipEnabled(e.target.checked)}
            className="sr-only peer"
            data-testid="tip-enabled-toggle"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-pink-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-pink-500"></div>
        </label>
      </div>

      {tipEnabled && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Minimum Tip Amount ($)
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">$</span>
            <Input
              type="number"
              min="1"
              step="0.01"
              value={minTipAmount}
              onChange={(e) => setMinTipAmount(parseFloat(e.target.value) || 2)}
              className="pl-8"
              data-testid="min-tip-amount"
            />
          </div>
          <p className="text-xs text-gray-500">
            Fans must tip at least this amount (minimum $2)
          </p>
        </div>
      )}
    </div>
  );
};

export default TipButton;
